'use client';

import { ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import aa from 'search-insights';

import EzShipCartImage from '@/assets/images/ez-ship-cart-icon.svg';
import Logo from '@/assets/images/logo.svg';
import HamburgerIcon from '@/assets/images/menu-01.svg';
import CartIcon from '@/assets/images/shopping-cart-v2-icon.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import RoqLogicLibraryProvider from '@/components/Providers/RoqLogicLibraryProvider';
import ShoppingCartProvider, { useShoppingCartContext } from '@/components/Providers/ShoppingCartProvider';
import { LoadingIcon } from '@/components/svg';
import SearchIcon from '@/components/svg/SearchIcon';
import Button from '@/components/V2/Button';
import DropdownHeader from '@/components/V2/DropdownDashboardHeader/Dropdown';
import TextInput from '@/components/V2/TextInput';
import { ALGOLIA_ANALYTICS_KEY, ALGOLIA_APPLICATION_ID, NEXT_MEDIA_PATH } from '@/config/env';
import { useDetectClickOutside } from '@/helpers/hooks';
import { useDebouncedValue } from '@/helpers/hooks/useDebounced';
import useEzShip from '@/helpers/hooks/useEzShip';
import { useLogout } from '@/helpers/hooks/useLogout';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { generateApproperiateURL } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import Bootstrap from '@/layouts/Bootstrap';
import { useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';

import { ForgotPasswordContainer } from '../../Auth';
import CartDropdown from '../../CartDropdown';
import DashboardSidebar from '../../DasboardSidebar';
import { DashboardProvider, useDashboardProvider } from '../../DasboardSidebar/DashboardProvider';
import AutoCompleteWrapper from '../../Header/SearchBar/AutoCompleteWrapper';
import SearchBarProvider, { useSearchContext } from '../../Header/SearchBar/SearchBarProvider';
import BackofficeLoginContainer from './BackofficeLoginContainer';

const Footer = dynamic(() => import('@/components/V2/Footer'));
const EzShipBanner = dynamic(() => import('@/components/EzShip/Banner'));
const AdminLogin = dynamic(() => import('../../Auth/AdminLogin'));
const FluidAppLogin = dynamic(() => import('../../Auth/FluidAppLogin'));
const LogoutComponent = dynamic(() => import('@/components/V2/Logout'));

// Optimization Changes Flag - Lazy load 2FA only when needed
const TwoFactorModalWrapper = dynamic(() => import('../../Auth/TwoFactorModalWrapper'), {
  loading: () => null,
  ssr: false,
});

const EzShipOrSimpleCart = () => {
  const { webalias, generateURL } = useWebAlias();
  const { ezShipId } = useEzShip();
  const { isBackOffice, setVisibilityOfEzShipSidebar } = useAppContext();
  const currentEzShip = useAppSelector(store => store.slices.ezShipSlice.currentEzShip);
  const totalItemsInEzShipCart = currentEzShip?.itemsCount;
  const { totalItemsInCart, cart, qualifiedItemsCount } = useShoppingCartContext();

  const isAuthenticated = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);
  const getCartUrl = () => {
    const baseCartUrl = '/cart';

    if (!isAuthenticated && typeof window !== 'undefined') {
      try {
        const currentPath = window.location.pathname;
        const currentParams = new URLSearchParams(window.location.search);
        const isGuestUser = currentParams.get('isGuestUser') === 'true';

        if (currentPath.includes('createrecommendationnew') || isGuestUser) {
          console.log('SimpleCart: Adding isGuestUser=true to cart URL');
          return `${baseCartUrl}?isGuestUser=true`;
        }
      } catch (error) {
        console.warn('SimpleCart: Error calculating cart URL:', error);
        return baseCartUrl;
      }
    }

    return baseCartUrl;
  };

  const cartUrl = getCartUrl();

  if (ezShipId) {
    return (
      <Link
        className="v2-relative"
        id="carticon"
        href="#"
        as={'a'}
        onClick={event => {
          event.preventDefault();
          if (+ezShipId == 0) {
            toast('Please add items to EzShip before accessing the cart.', { type: 'error' });
            return;
          }

          setVisibilityOfEzShipSidebar(true);
        }}
      >
        <Image src={EzShipCartImage} loading="lazy" alt="Cart Icon" className="v2-w-6 lg:v2-w-[30px]" />
        {totalItemsInEzShipCart || 0}
      </Link>
    );
  }
  return (
    <Link
      href={generateApproperiateURL(cartUrl, isBackOffice ? 'shopping' : webalias)}
      id="carticon"
      className="v2-relative"
    >
      <CartDropdown
        carts={cart}
        qualifiedItemsCount={qualifiedItemsCount ?? 0}
        totalItems={totalItemsInCart ?? 0}
        isShortHeaderCart={true}
      >
        <div className="v2-text-sm v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-flex v2-items-center v2-gap-1">
          <Image src={CartIcon} loading="lazy" alt="Cart Icon" />
          {totalItemsInCart || 0}
        </div>
      </CartDropdown>
    </Link>
  );
};

const BackofficeSearchBar = () => {
  const {
    value,
    onSearchChange,
    onSearch,
    getInputProps,
    closeAutoCompleteMenu,
    isSearchBarFocused,
    setSearchBarFocused,
  } = useSearchContext();
  const search = useAppSelector(store => store.slices.searchSlice);
  const searchBarInputRef = useRef<HTMLInputElement | null>(null);
  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = useMemo(
    () =>
      getInputProps({
        inputElement: searchBarInputRef.current,
      }),
    [getInputProps]
  );

  const debouncedValue = useDebouncedValue(value, 500);
  useEffect(() => {
    onChange({
      target: { value: debouncedValue },
      currentTarget: { value: debouncedValue },
    } as unknown as Event);
  }, [debouncedValue]);

  return (
    <div className="v2-flex-grow v2-relative v2-flex md:v2-flex sm:v2-hidden xs:v2-hidden">
      <TextInput
        {...inputProps}
        value={value}
        ref={searchBarInputRef as any}
        type="text"
        placeholder="Search Here"
        inputClassName="v2-h-[40px] v2-shadow-xs v2-rounded-md"
        onChange={e => {
          onSearchChange(e);
        }}
        onClick={e => {
          setSearchBarFocused(true);
          onClick(e as unknown as MouseEvent);
        }}
        onFocus={e => {
          setSearchBarFocused(true);
          if (!isSearchBarFocused) onFocus(e as unknown as Event);
          if (value.length > 0) {
            const target = { value };
            const event = { target, currentTarget: target, relatedTarget: target };

            onChange(event as unknown as Event);
          }
        }}
        onKeyDown={e => {
          onKeyDown(e as unknown as KeyboardEvent);
          if (e.key === 'Enter') {
            if (!value) return;
            onSearch();
            closeAutoCompleteMenu();
            setSearchBarFocused(false);
            // addSearchQueryToRecentSearches(value);
          }
        }}
        onCompositionEnd={e => onCompositionEnd(e as unknown as Event)}
      />
      <Button
        btnSize="sm"
        scheme="primary"
        type="button"
        variant="filled"
        className="v2-flex v2-justify-center v2-items-center v2-h-[40px] v2-rounded-l-t-0 v2-rounded-tl-none v2-rounded-bl-none v2-absolute v2-right-0 v2-top-0 !v2-px-[10px]"
        onClick={() => {
          if (!value) {
            try {
              document.getElementById('search-from-header')?.focus();
            } catch (error) {
              console.error(error);
            }
            return setSearchBarFocused(true);
          }
          onSearch();
          closeAutoCompleteMenu();
          // addSearchQueryToRecentSearches(value);
        }}
      >
        {/* <Image src={SearchIcon} alt="Search Icon" /> */}
        {search?.isLoading ? <LoadingIcon /> : <SearchIcon color="white" />}
      </Button>
      {isSearchBarFocused && <AutoCompleteWrapper isDashboard={true} />}
    </div>
  );
};

const BackofficeTopNav = () => {
  const { toggleMenu, setToggleMenu } = useDashboardProvider();
  const { customer, isLoggedIn } = useAuthContext();
  const { webalias, generateURL } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { logout } = useLogout(isBackOffice, webalias);

  const {
    value,
    onSearchChange,
    onSearch,
    getInputProps,
    closeAutoCompleteMenu,
    isSearchBarFocused,
    setSearchBarFocused,
  } = useSearchContext();
  const search = useAppSelector(store => store.slices.searchSlice);
  const searchBarInputRef = useRef<HTMLInputElement | null>(null);
  const searchBarContainer = useRef<HTMLInputElement | null>(null);
  const [searchbarHide, setSearchbarHide] = useState(false);

  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = useMemo(
    () =>
      getInputProps({
        inputElement: searchBarInputRef.current,
      }),
    [getInputProps]
  );

  useDetectClickOutside(() => {
    setSearchbarHide(false);
  }, searchBarContainer);

  const debouncedValue = useDebouncedValue(value, 500);
  useEffect(() => {
    onChange({
      target: { value: debouncedValue },
      currentTarget: { value: debouncedValue },
    } as unknown as Event);
  }, [debouncedValue]);

  useEffect(() => {
    aa('init', {
      appId: ALGOLIA_APPLICATION_ID,
      apiKey: ALGOLIA_ANALYTICS_KEY,
      useCookie: true,
    });
    window.aa = aa;
  }, []);

  const userSettings = useMemo(() => {
    return [
      { name: 'View Profile', link: `/profile?token=${customer?.token ?? ''}` },
      { name: 'EZ Ships', link: '/AutoOrders/auto-order-list' },
      // { name: 'Manage Custom Food Labels', link: '/foodlabel/managelabels' },
      { name: 'Orders', link: '/orders' },
      { name: 'Account Settings', link: '/account/settings' },
      {
        name: 'Sign out',
        link: '#',
        as: 'a',
        onClick: (e: MouseEvent) => {
          e.preventDefault();
          console.log('BackofficeTopNav: Sign out clicked, calling logout...');
          try {
            logout();
          } catch (error) {
            console.error('BackofficeTopNav: Logout error:', error);
          }
        },
      },
    ] as CustomLink[];
  }, [customer?.token, generateURL, webalias, logout]);

  const backOfficeUserName =
    customer?.fullName && customer?.customerID ? `Hi, ${customer?.firstName} ${customer?.lastName}` : '';

  const profileLink = customer?.avatarUrl ? `${NEXT_MEDIA_PATH}${customer.avatarUrl ?? ''}` : '';

  let logoRedirectLink = isBackOffice
    ? `https://shop.pawtree.com/${customer?.webAlias ?? ''}`
    : generateApproperiateURL('/', webalias, isBackOffice);

  return (
    <div className="v2-px-8 md:v2-px-8 sm:v2-px-4 xs:v2-px-4 v2-pt-4 v2-bg-white v2-sticky v2-top-0 v2-z-[2] v2-w-full v2-border-b v2-border-[#EAECF0]">
      <div className="v2-flex md:v2-flex sm:v2-grid xs:v2-grid v2-grid-cols-3 v2-items-center v2-gap-[30px] md:v2-gap-[30px] sm:v2-gap-[10px] xs:v2-gap-[10px] v2-justify-self-auto md:v2-justify-self-auto sm:v2-justify-between xs:v2-justify-between v2-pb-4">
        <BackofficeSearchBar />

        <div className="v2-hidden md:v2-hidden sm:v2-flex xs:v2-flex">
          <Image onClick={() => setToggleMenu(!toggleMenu)} src={HamburgerIcon} alt="" />
        </div>
        <div className="v2-hidden md:v2-hidden sm:v2-flex xs:v2-flex">
          <Link href={logoRedirectLink} target="_blank" className="v2-max-w-[110px] v2-w-full v2-flex v2-mx-auto">
            <Image src={Logo} alt="Logo" />
          </Link>
        </div>
        <div className="v2-flex v2-items-center v2-gap-6 md:v2-gap-6 sm:v2-gap-2 xs:v2-gap-2 v2-justify-end">
          <Link href="/account/earnings-account?pointAccountID=1">
            <div className="v2-flex v2-items-center v2-gap-1">
              <div className="v2-h-[41px] md:v2-h-[41px] sm:v2-h-auto xs:v2-h-auto v2-w-[41px] md:v2-w-[41px] sm:v2-w-auto xs:v2-w-auto v2-rounded-full md:v2-rounded-full sm:v2-rounded-xs xs:v2-rounded-xs v2-flex v2-items-center v2-justify-center v2-bg-primary v2-px-[10px] v2-py-[10px] md:v2-px-[10px] md:v2-py-[10px] sm:v2-px-2 sm:v2-py-[2px] xs:v2-px-2 xs:v2-py-[2px]">
                <p className="v2-text-sm md:v2-text-sm sm:v2-text-xs xs:v2-text-xs v2-font-normal v2-font-sweet-sans-pro v2-text-white v2-flex v2-items-center">
                  PP
                  <span className="v2-hidden md:v2-hidden sm:v2-flex xs:v2-flex v2-ml-[2px]">
                    {customer?.pawPoints || 0}
                  </span>
                </p>
              </div>
              <p className="v2-text-sm v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-flex md:v2-flex sm:v2-hidden xs:v2-hidden">
                {customer?.pawPoints || 0}
              </p>
            </div>
          </Link>

          <div className="">
            <ShoppingCartProvider>
              <EzShipOrSimpleCart />
            </ShoppingCartProvider>
          </div>
          <div className="v2-flex md:v2-flex sm:v2-hidden xs:v2-hidden">
            <DropdownHeader
              options={userSettings}
              heading={customer?.company ?? customer?.fullName ?? ''}
              customerId={customer?.customerID ?? 0}
              avatorURL={profileLink}
              name={backOfficeUserName}
            />
          </div>
        </div>
      </div>

      <div className="v2-border-t v2-border-[#EAECF0] v2-py-2 v2-hidden md:v2-hidden sm:v2-flex xs:v2-flex v2-justify-between v2-items-center v2-relative">
        <div>
          <DropdownHeader
            options={userSettings}
            heading={customer?.company ?? customer?.fullName ?? ''}
            customerId={customer?.customerID ?? 0}
            avatorURL={profileLink}
            name={backOfficeUserName}
          />
        </div>
        <div
          className={cn('v2-flex-grow v2-flex ', {
            'v2-absolute v2-left-0 v2-w-full v2-transition-all': isSearchBarFocused,
          })}
          ref={searchBarContainer}
        >
          <TextInput
            {...inputProps}
            value={value}
            ref={searchBarInputRef as any}
            type="text"
            placeholder="Search Here"
            className={!isSearchBarFocused ? '!v2-w-0' : '!v2-h-full'}
            inputClassName={
              !isSearchBarFocused
                ? '!v2-w-0 !v2-p-0 !v2-border-0'
                : 'v2-h-[36px] !v2-w-full v2-rounded-xs !v2-rounded-tr-none !v2-roundded-br-none'
            }
            onChange={e => {
              onSearchChange(e);
            }}
            onClick={e => {
              setSearchBarFocused(true);
              onClick(e as unknown as MouseEvent);
            }}
            onFocus={e => {
              setSearchBarFocused(true);
              if (!isSearchBarFocused) onFocus(e as unknown as Event);
              if (value.length > 0) {
                const target = { value };
                const event = { target, currentTarget: target, relatedTarget: target };

                onChange(event as unknown as Event);
              }
            }}
            onKeyDown={e => {
              onKeyDown(e as unknown as KeyboardEvent);
              if (e.key === 'Enter') {
                if (!value) return;
                onSearch();
                closeAutoCompleteMenu();
                setSearchBarFocused(false);
                // addSearchQueryToRecentSearches(value);
              }
            }}
            onCompositionEnd={e => onCompositionEnd(e as unknown as Event)}
          />
          <Button
            btnSize="sm"
            scheme="primary"
            type="button"
            variant="filled"
            className={
              isSearchBarFocused
                ? 'v2-rounded-l-t-0 v2-h-[36px] v2-rounded-tl-none v2-rounded-bl-none v2-absolute v2-right-0 v2-top-0 !v2-p-2'
                : 'v2-h-[36px] !v2-p-2 v2-ml-auto'
            }
            onClick={() => {
              if (!value || !searchbarHide) {
                try {
                  document.getElementById('search-from-header')?.focus();
                } catch (error) {
                  console.error(error);
                }
                setSearchbarHide(true);
                return setSearchBarFocused(true);
              }
              onSearch();
              closeAutoCompleteMenu();
              setSearchbarHide(false);
              // addSearchQueryToRecentSearches(value);
            }}
          >
            {/* <Image src={SearchIcon} alt="Search Icon" /> */}
            {search?.isLoading ? <LoadingIcon width="20" height="20" /> : <SearchIcon color="white" />}
          </Button>
        </div>
        {isSearchBarFocused && <AutoCompleteWrapper isDashboard={true} isMobileDashboard />}
      </div>
    </div>
  );
};

const BackofficeSection = ({ children, showEzShipBanner }: { children: ReactNode; showEzShipBanner?: boolean }) => {
  const { toggleMenu, setToggleMenu } = useDashboardProvider();
  return (
    <>
      <div
        className={cn('v2-bg-white v2-flex-1 v2-flex v2-flex-col v2-transition-all v2-duration-300 v2-ease-in-out', {
          'v2-ml-[74px] lg:v2-ml-[74px] md:v2-ml-[74px] sm:v2-ml-0 xs:v2-ml-0': toggleMenu,
          'v2-ml-[316px] 2xl:v2-ml-[316px] xl:v2-ml-[74px] lg:v2-ml-[74px] md:v2-ml-[74px] sm:v2-ml-0 xs:v2-ml-0':
            !toggleMenu,
        })}
      >
        <SearchBarProvider>
          <BackofficeTopNav />
        </SearchBarProvider>
        {showEzShipBanner && (
          <Suspense>
            <EzShipBanner />
          </Suspense>
        )}
        <Suspense key={'backoffice-the-actual-page-loader'}>{children}</Suspense>
        {/* {children} */}
      </div>
      {!toggleMenu && (
        <div className="v2-fixed 2xl:v2-hidden sm:v2-block xs:v2-block md:v2-block v2-block v2-w-full v2-h-full v2-bg-[#0C111D80] v2-top-0 v2-left-0 v2-right-0 v2-bottom-0 v2-z-[2]" />
      )}
    </>
  );
};

const BackofficeLayout = ({ showEzShipBanner, children }: { children: ReactNode; showEzShipBanner?: boolean }) => {
  const pathname = usePathname();

  useEffect(() => {
    const isRoot = pathname === '/';
    const isReport = pathname === '/pawtrip-report';
    const profile = pathname === '/profile';
    if (isRoot || isReport || profile) {
      document.body.style.backgroundColor = '#F9FAFB';
    } else {
      document.body.style.backgroundColor = 'unset';
    }
  }, [pathname]);

  if (pathname === '/login') {
    return (
      <>
        {/* Optimization Changes Flag - Use wrapper for conditional 2FA loading */}
        <TwoFactorModalWrapper />
        <BackofficeLoginContainer />
      </>
    );
  }

  if (pathname === '/forgotpassword') {
    return <ForgotPasswordContainer />;
  }

  if (pathname.toLowerCase().startsWith('/adminlogin')) {
    return <AdminLogin />;
  }

  if (pathname.toLowerCase().startsWith('/silentlogin')) {
    return <FluidAppLogin />;
  }

  if (pathname.toLowerCase().startsWith('/logout')) {
    return <LogoutComponent />;
  }

  return (
    <DashboardProvider>
      <RoqLogicLibraryProvider>
        {/* Optimization Changes Flag - Use wrapper for conditional 2FA loading */}
        <TwoFactorModalWrapper />
        <div className="v2-min-h-screen v2-contents">
          <DashboardSidebar />
          <BackofficeSection showEzShipBanner={showEzShipBanner}>{children}</BackofficeSection>
        </div>

        <Footer isDashboard={true} />

        <Bootstrap />
      </RoqLogicLibraryProvider>
    </DashboardProvider>
  );
};

export default BackofficeLayout;
